// Notification bell — Substep 5-η.6 moved into TopBar (inline flex
// element). Shows an unread-count badge that polls
// /api/notifications/count every 30s. Click to open a dropdown with
// the recent notifications, grouped by relative-time bucket and
// filterable by category (Documents / Extractions / System / Stage).
// Each entry can be clicked to navigate to the linked matter,
// dismissed individually, or batch-cleared via "Mark all read" +
// "Clear read."

import React, { useState, useEffect, useCallback, useRef } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Spinner } from "./Spinner.jsx";

const NOTIF_ICONS = {
  doc: I.doc, scan: I.scan, zap: I.zap, folder: I.folder, alert: I.alert, check: I.check,
};

// Maps a notification's `category` (set on the backend in
// app/services/notifications.py NOTIFICATION_EVENTS) to a chip in the
// panel. Order here is the order chips are rendered.
const NOTIF_CATEGORY_CHIPS = [
  { key: "all",        label: "All",         match: () => true },
  { key: "doc",        label: "Documents",   match: (c) => c === "doc" || c === "batch" },
  { key: "extraction", label: "Extractions", match: (c) => c === "extraction" },
  { key: "system",     label: "System",      match: (c) => c === "system" },
  { key: "stage",      label: "Stage",       match: (c) => c === "stage" },
];

// Bucket a notification into a relative-time group used as a section
// header in the panel. Same calendar day = Today; previous calendar
// day = Yesterday; same calendar week and older than yesterday = This
// week; anything else = Earlier.
function notifTimeBucket(isoDate) {
  if (!isoDate) return "Earlier";
  const ts = new Date(isoDate);
  const now = new Date();
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(ts, now)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(ts, yesterday)) return "Yesterday";
  // "This week" = same ISO week (Mon–Sun) and not Today/Yesterday.
  // Use a 7-day rolling window for simplicity — close enough.
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(now.getDate() - 7);
  if (ts >= sevenDaysAgo) return "This week";
  return "Earlier";
}

export function NotificationBell({ onNavigateToMatter }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const bellRef = useRef(null);

  // Poll for unread count every 30 seconds
  const pollCount = useCallback(() => {
    apiFetch("/api/notifications/count")
      .then(data => setUnreadCount(data.unread_count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    pollCount();
    const interval = setInterval(pollCount, 30000);
    return () => clearInterval(interval);
  }, [pollCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const resp = await apiFetch("/api/notifications?limit=20");
      setNotifications(resp.data || []);
      setUnreadCount(resp.unread_count || 0);
    } catch (e) { console.error("Failed to load notifications:", e); }
    setLoading(false);
  };

  const handleOpen = () => {
    if (!open) loadNotifications();
    setOpen(!open);
  };

  const handleMarkAllRead = async () => {
    await apiFetch("/api/notifications/mark-all-read", { method: "POST" });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  // Bulk-dismiss all read notifications. Companion to "Mark all read"
  // — keeps the panel from accumulating triaged items the user has
  // already seen but not yet manually dismissed.
  const handleClearRead = async () => {
    try {
      await apiFetch("/api/notifications/clear-read", { method: "POST" });
      setNotifications(prev => prev.filter(n => !n.is_read));
    } catch (e) { console.error("Clear-read failed:", e); }
  };

  const handleDismiss = async (id, e) => {
    e.stopPropagation();
    await apiFetch(`/api/notifications/${id}`, { method: "DELETE" });
    setNotifications(prev => prev.filter(n => n.id !== id));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      await apiFetch("/api/notifications/mark-read", {
        method: "POST",
        body: JSON.stringify({ notification_ids: [notif.id] }),
      });
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    // Navigate if linked to matter
    if (notif.link_matter_id && onNavigateToMatter) {
      onNavigateToMatter({ id: notif.link_matter_id, client: { name: notif.matter_name } });
      setOpen(false);
    }
  };

  const timeAgo = (isoDate) => {
    if (!isoDate) return "";
    const diff = Date.now() - new Date(isoDate).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // Apply category filter, then group by relative-time bucket. Order
  // of buckets matters — render in this order: Today, Yesterday, This
  // week, Earlier.
  const activeChip = NOTIF_CATEGORY_CHIPS.find(c => c.key === categoryFilter) || NOTIF_CATEGORY_CHIPS[0];
  const filteredNotifs = notifications.filter(n => activeChip.match(n.category || ""));
  const timeBuckets = ["Today", "Yesterday", "This week", "Earlier"];
  const grouped = timeBuckets.map(label => ({
    label,
    items: filteredNotifs.filter(n => notifTimeBucket(n.created_at) === label),
  })).filter(g => g.items.length > 0);
  const hasReadItem = notifications.some(n => n.is_read);

  return React.createElement("div", { ref: bellRef, style: { position:"relative" } },
    // Bell button (Substep 5-η.6: sized for TopBar host, not the
    // old floating-button footprint).
    React.createElement("button", {
      onClick: handleOpen,
      "aria-label": `Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`,
      title: `Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`,
      style: {
        width:"34px", height:"34px", borderRadius:"8px",
        border: open ? `1px solid ${C.gold}` : `1px solid transparent`,
        background: open ? `${C.gold}10` : "transparent",
        cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
        position:"relative",
        transition:"all 0.15s",
      },
      onMouseEnter: e => { if (!open) e.currentTarget.style.background = C.warmWhite; },
      onMouseLeave: e => { if (!open) e.currentTarget.style.background = "transparent"; },
    },
      // Bell SVG
      React.createElement("svg", {width:18,height:18,viewBox:"0 0 24 24",fill:"none",stroke:C.navy,strokeWidth:2,strokeLinecap:"round"},
        React.createElement("path", {d:"M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"}),
        React.createElement("path", {d:"M13.73 21a2 2 0 0 1-3.46 0"}),
      ),
      // Unread badge
      unreadCount > 0 && React.createElement("div", {
        style: {
          position:"absolute", top:"-4px", right:"-4px", minWidth:"18px", height:"18px",
          borderRadius:"9px", background:C.error, color:"#fff",
          fontSize:"10px", fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center",
          padding:"0 4px", border:"2px solid #fff",
        }
      }, unreadCount > 99 ? "99+" : unreadCount),
    ),

    // Dropdown
    open && React.createElement("div", {
      style: {
        position:"absolute", top:"48px", right:0, width:"380px", maxHeight:"480px",
        background:"#fff", borderRadius:"12px", border:`1px solid ${C.warmGray}`,
        boxShadow:"0 12px 48px rgba(0,0,0,0.15)", display:"flex", flexDirection:"column",
        overflow:"hidden",
      }
    },
      // Header
      React.createElement("div", {
        style: {
          display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"14px 16px", borderBottom:`1px solid ${C.warmGray}40`,
        }
      },
        React.createElement("span", {
          style: { fontSize:"14px", fontWeight:700, color:C.navy }
        }, "Notifications"),
        React.createElement("div", { style: { display:"flex", gap:"12px", alignItems:"center" } },
          // Manual refresh — polling fires every 30s but staff sometimes
          // want to pull the latest immediately (e.g., after triggering
          // a batch generation that produces notifications on completion).
          // Spin the icon while a fetch is in flight so the click feels
          // acknowledged.
          React.createElement("button", {
            onClick: () => { loadNotifications(); pollCount(); },
            title: "Refresh notifications",
            "aria-label": "Refresh notifications",
            disabled: loading,
            style: {
              border:"none", background:"none", cursor: loading ? "not-allowed" : "pointer",
              padding:"2px", color: C.textMuted, opacity: loading ? 0.5 : 1,
              display:"flex", alignItems:"center",
            },
          },
            React.createElement("svg", {
              width:14, height:14, viewBox:"0 0 24 24", fill:"none",
              stroke:"currentColor", strokeWidth:2, strokeLinecap:"round",
              style: loading ? { animation: "spin 1s linear infinite" } : null,
            },
              React.createElement("polyline", {points:"23 4 23 10 17 10"}),
              React.createElement("path", {d:"M20.49 15a9 9 0 11-2.12-9.36L23 10"}),
            ),
          ),
          unreadCount > 0 && React.createElement("button", {
            onClick: handleMarkAllRead,
            style: {
              border:"none", background:"none", cursor:"pointer",
              fontSize:"12px", color:C.gold, fontWeight:600, padding:0,
            }
          }, "Mark all read"),
          hasReadItem && React.createElement("button", {
            onClick: handleClearRead,
            title: "Dismiss every notification you've already read",
            style: {
              border:"none", background:"none", cursor:"pointer",
              fontSize:"12px", color:C.textMuted, fontWeight:500, padding:0,
            }
          }, "Clear read"),
        ),
      ),

      // Category filter chips
      React.createElement("div", {
        style: {
          display:"flex", gap:"6px", padding:"10px 12px",
          borderBottom:`1px solid ${C.warmGray}40`, overflowX:"auto",
        }
      },
        ...NOTIF_CATEGORY_CHIPS.map(chip => {
          const active = chip.key === categoryFilter;
          return React.createElement("button", {
            key: chip.key,
            onClick: () => setCategoryFilter(chip.key),
            style: {
              flexShrink: 0,
              padding: "4px 12px", borderRadius: "9999px",
              border: `1px solid ${active ? C.gold : C.warmGray}`,
              background: active ? `${C.gold}15` : "#fff",
              color: active ? C.gold : C.textMuted,
              fontSize: "12px", fontWeight: 600, cursor: "pointer",
              whiteSpace: "nowrap",
            },
          }, chip.label);
        }),
      ),

      // Notification list (now grouped by relative-time bucket)
      React.createElement("div", { style: { flex:1, overflowY:"auto" } },
        loading && React.createElement("div", {
          style: { padding:"30px", textAlign:"center" }
        }, React.createElement(Spinner, { size: 20 })),

        !loading && filteredNotifs.length === 0 && React.createElement("div", {
          style: { padding:"40px 20px", textAlign:"center", color:C.textLight, fontSize:"13px" }
        }, notifications.length === 0
          ? "No notifications yet."
          : `No ${activeChip.label.toLowerCase()} notifications.`),

        ...(!loading ? grouped.map(group =>
          React.createElement("div", { key: group.label },
            React.createElement("div", {
              style: {
                padding: "8px 16px 4px", fontSize: "10px", textTransform: "uppercase",
                fontWeight: 700, letterSpacing: "0.06em", color: C.textLight,
                background: `${C.warmGray}20`,
              }
            }, group.label),
            ...group.items.map(n =>
              React.createElement("div", {
                key: n.id,
                onClick: () => handleClick(n),
                style: {
                  display:"flex", gap:"10px", padding:"12px 16px",
                  borderBottom:`1px solid ${C.warmGray}20`,
                  background: n.is_read ? "transparent" : `${C.gold}06`,
                  cursor: n.link_matter_id ? "pointer" : "default",
                  transition:"background 0.15s",
                },
                onMouseEnter: e => { if (n.link_matter_id) e.currentTarget.style.background = C.warmWhite; },
                onMouseLeave: e => { e.currentTarget.style.background = n.is_read ? "transparent" : `${C.gold}06`; },
              },
                // Icon
                React.createElement("div", {
                  style: {
                    width:"32px", height:"32px", borderRadius:"8px", flexShrink:0,
                    background: n.is_read ? `${C.warmGray}40` : `${C.gold}15`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color: n.is_read ? C.textLight : C.gold,
                  }
                }, NOTIF_ICONS[n.icon] || NOTIF_ICONS.doc),
                // Content
                React.createElement("div", { style: { flex:1, minWidth:0 } },
                  React.createElement("div", {
                    style: {
                      fontSize:"13px", fontWeight: n.is_read ? 400 : 600, color:C.navy,
                      whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
                    }
                  }, n.title),
                  React.createElement("div", {
                    style: {
                      fontSize:"12px", color:C.textMuted, marginTop:"2px",
                      display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden",
                    }
                  }, n.message),
                  React.createElement("div", {
                    style: { display:"flex", gap:"8px", alignItems:"center", marginTop:"4px" }
                  },
                    React.createElement("span", {
                      style: { fontSize:"11px", color:C.textLight }
                    }, timeAgo(n.created_at)),
                    n.matter_name && React.createElement("span", {
                      style: { fontSize:"10px", color:C.gold, fontWeight:500 }
                    }, n.matter_name),
                  ),
                ),
                // Unread dot + dismiss
                React.createElement("div", { style: { display:"flex", flexDirection:"column", alignItems:"center", gap:"6px", flexShrink:0 } },
                  !n.is_read && React.createElement("div", {
                    style: { width:"8px", height:"8px", borderRadius:"50%", background:C.gold, marginTop:"4px" }
                  }),
                  React.createElement("button", {
                    onClick: (e) => handleDismiss(n.id, e),
                    title: "Dismiss",
                    style: {
                      border:"none", background:"none", cursor:"pointer", color:C.textLight,
                      fontSize:"14px", padding:"0 2px", lineHeight:1, opacity:0.5,
                    },
                    onMouseEnter: e => { e.currentTarget.style.opacity = 1; },
                    onMouseLeave: e => { e.currentTarget.style.opacity = 0.5; },
                  }, "×"),
                ),
              ),
            ),
          ),
        ) : []),
      ),
    ),
  );
}
